import {FunctionComponent, useCallback, useMemo, useState} from 'react';
import clsx from 'clsx';
import {twMerge} from 'tailwind-merge';
import ArrowIcon from '@/assets/images/icon-arrow.svg?react';
import {useDocumentLocale} from '@/components/document/hooks';
import {TrackedButton} from '@/components/tracked-atoms/tracked-button';
import {DocumentSectionExcerpt} from '@/engine/types/document-model';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import Backdrop from './backdrop';
import SectionsSelectorMenu from './menu';

interface ElementProps {
  activeSectionIndex: number;
  currentSectionIndex: number;
  isDisabled?: boolean;
  sections: DocumentSectionExcerpt[];
}

const SectionsSelector: FunctionComponent<ElementProps> = ({
  activeSectionIndex,
  currentSectionIndex,
  isDisabled = false,
  sections,
}) => {
  const {t} = useDocumentLocale();

  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const label = useMemo(() => {
    if (activeSectionIndex === -1) {
      return t('components.sections-selector.summary.label');
    }
    return `${t('components.sections-selector.button.label')} ${activeSectionIndex + 1} ⏤ ${sections.length}`;
  }, [activeSectionIndex, sections.length, t]);

  const onShowMenu = useCallback(() => {
    setIsMenuVisible(!isMenuVisible);
  }, [isMenuVisible]);

  const onHideMenu = useCallback(() => {
    setIsMenuVisible(false);
  }, []);

  const buttonClassName = clsx(
    'group flex items-center gap-2 text-lg text-black transition-colors hover:text-violet disabled:text-grey-400',
    isDisabled && 'cursor-not-allowed',
  );

  return (
    <>
      <div className="relative flex min-h-10" ref={setContainer}>
        <TrackedButton
          data-testid="document-sections-selector-toggle"
          aria-label={t('components.sections-selector.button.accessibility')}
          className={twMerge(buttonClassName)}
          onClick={onShowMenu}
          disabled={isDisabled}
          trackingKey={WEB_CLICK_TRACKING_KEY.OPEN_SECTIONS_MENU}
          trackingAs={TrackingClickPrefix.CONTENT}
        >
          {label}

          <ArrowIcon
            className={twMerge(
              clsx(
                'h-3 fill-violet transition-transform duration-200 ease-in-out',
                isMenuVisible && 'rotate-180',
                isDisabled && 'fill-grey-400',
              ),
            )}
          />
        </TrackedButton>

        {isMenuVisible && (
          <SectionsSelectorMenu
            activeSectionIndex={activeSectionIndex}
            clickOutsideElement={container}
            currentSectionIndex={currentSectionIndex}
            onClose={onHideMenu}
            sections={sections}
          />
        )}
      </div>

      <Backdrop onClick={onHideMenu} isVisible={isMenuVisible} />
    </>
  );
};

export default SectionsSelector;
